import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import client from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/StatusBadge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Loader2 } from 'lucide-react';

export default function Todos() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => client.get('/todos').then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const toggle = async (t) => { await client.post(`/todos/${t.id}`, { status: t.status === 'done' ? 'open' : 'done' }); load(); };
  const bucket = (days) => days == null ? 'Company' : days < 0 ? 'Overdue' : days <= 5 ? '< 5 Days' : days <= 10 ? '5-10 Days' : '30+ Days';
  return (
    <AppShell title="To-Dos">
      <div className="mb-4 flex justify-end"><Button onClick={() => setOpen(true)} data-testid="todo-add-button"><Plus className="mr-1.5 h-4 w-4" />Add To-Do</Button></div>
      {items === null ? <div className="space-y-2">{[1,2,3].map(i=><Skeleton key={i} className="h-14" />)}</div> : items.length === 0 ? <Card className="p-12 text-center text-sm text-muted-foreground">No to-dos.</Card> : (
        <div className="space-y-2">{items.map((t) => (
          <Card key={t.id} className="flex items-center gap-3 p-3.5" data-testid={`todo-${t.id}`}>
            <Checkbox checked={t.status === 'done'} onCheckedChange={() => toggle(t)} data-testid={`todo-check-${t.id}`} />
            <div className="flex-1"><div className={`font-medium ${t.status === 'done' ? 'text-muted-foreground line-through' : ''}`}>{t.title}</div><div className="text-xs text-muted-foreground">{t.category}{t.due_date ? ` · due ${new Date(t.due_date).toLocaleDateString()}` : ''}</div></div>
            <StatusBadge status={t.days_to_due != null && t.days_to_due < 0 ? 'expired' : t.days_to_due != null && t.days_to_due <= 5 ? 'warning' : 'pending'} label={bucket(t.days_to_due)} />
          </Card>
        ))}</div>
      )}
      <TodoDialog open={open} onOpenChange={setOpen} onAdded={load} />
    </AppShell>
  );
}

function TodoDialog({ open, onOpenChange, onAdded }) {
  const [f, setF] = useState({ title: '', category: 'Company', due_date: '' });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!f.title) { toast.error('Title required'); return; }
    setLoading(true);
    try { await client.post('/todos', { title: f.title, category: f.category, due_date: f.due_date ? new Date(f.due_date).toISOString() : null }); toast.success('Added'); onAdded(); onOpenChange(false); setF({ title: '', category: 'Company', due_date: '' }); }
    catch { toast.error('Failed'); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent><DialogHeader><DialogTitle>Add To-Do</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Title *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1" data-testid="todo-title-input" /></div>
        <div><Label>Category</Label><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{['Company', 'Driver', 'Fleet', 'Training'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Due Date</Label><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="mt-1" /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={loading} data-testid="todo-submit-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Add</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}
